import { isPublicAudience } from '@/utils/publicAccess';
import { installPublicMapPagination } from './publicMapPagination';

const identityKey = (browser) => `${browser?.catalog?.browser_storage_prefix ?? 'catalog'}:browser-identity`;

export function pageIdentity(page) {
    return JSON.stringify([
        page?.props?.tenant?.id ?? page?.props?.tenant_id ?? page?.props?.tenant_name ?? '',
        page?.props?.auth?.user?.id ?? null,
        isPublicAudience(page) ? 'public' : 'authenticated',
    ]);
}

export function installPublicSessionGuard({ router, initialPage, reset, browser = window, http }) {
    const IDENTITY_KEY = identityKey(browser);
    let page = initialPage;
    let requestLocale = page?.props?.localization?.locale ?? 'it';
    let identity = pageIdentity(page);
    const enabled = () => page?.props?.publicAccess?.enabled === true;
    const readIdentity = () => {
        try { return browser.localStorage.getItem(IDENTITY_KEY); } catch { return null; }
    };
    const rememberIdentity = () => {
        try { browser.localStorage.setItem(IDENTITY_KEY, identity); } catch { /* Storage may be disabled. */ }
    };
    const clear = () => {
        reset();
        router.clearHistory();
        try { browser.sessionStorage.clear(); } catch { /* Storage may be disabled. */ }
    };
    const reload = (url = browser.location.href) => {
        clear();
        browser.history.replaceState({}, '', url);
        browser.location.replace(url);
    };
    if (enabled()) {
        if (readIdentity() !== null && readIdentity() !== identity) clear();
        rememberIdentity();
    }
    router.on('before', event => {
        if (enabled() && event.detail.visit.method !== 'get'
            && new URL(event.detail.visit.url, browser.location.href).pathname === '/logout') clear();
    });
    // Synchronous page watchers reload open sheets while Inertia swaps props,
    // before navigate fires. Those API reads must already use the new language.
    router.on('beforeUpdate', event => {
        requestLocale = event.detail.page?.props?.localization?.locale ?? 'it';
    });
    router.on('navigate', event => {
        const next = event.detail.page;
        const nextIdentity = pageIdentity(next);
        // Login pages also receive public settings when public access is off.
        // Refresh the document on every identity change to replace that bootstrap context.
        const changed = nextIdentity !== identity;
        page = next;
        requestLocale = next?.props?.localization?.locale ?? 'it';
        identity = nextIdentity;
        if (enabled() || changed) rememberIdentity();
        if (changed) reload(next.url);
    });
    // Restore neither a cached DOM from before logout nor another tab's identity.
    browser.addEventListener('pageshow', event => {
        if (enabled() && (event.persisted || (readIdentity() !== null && readIdentity() !== identity))) reload();
    });
    browser.addEventListener('storage', event => {
        if (enabled() && event.key === IDENTITY_KEY && event.newValue !== identity) reload();
    });
    installPublicMapPagination({ http, isPublic: () => isPublicAudience(page), identity: () => identity, origin: browser.location.origin });
    http?.interceptors.request.use(config => {
        if (!config?.url) return config;
        const url = new URL(config.url, new URL(config.baseURL || browser.location.origin, browser.location.origin));
        if (url.origin !== browser.location.origin) return config;
        const needsLocale = isPublicAudience(page)
            ? /^\/(api|database)(\/|$)/.test(url.pathname)
            : (page.props?.tenant?.theme ?? page.props?.tenant_theme) === 'Pompei'
                && /^\/api(\/|$)/.test(url.pathname);
        if (!needsLocale) return config;
        const lang = requestLocale;
        if (config.params instanceof URLSearchParams) {
            const params = new URLSearchParams(config.params);
            if (!params.has('lang')) params.set('lang', lang);
            config.params = params;
        } else config.params = { lang, ...config.params };
        return config;
    });
}
